import { API, graphqlOperation } from "aws-amplify";
import * as subscriptions from "@/graphql/subscriptions";

/**
 * 外販送信ステータス
 */
export type SaleStatus = 'processing' | 'success' | 'failure' | 'error';

/**
 * 外販送信ステータス更新データ
 */
export interface SaleStatusUpdate {
    requestId: string;
    fileId: string;
    status: SaleStatus;
    saleCount?: number;
}

/**
 * 外販送信ステータス更新コールバック
 */
export type SaleStatusCallback = (data: SaleStatusUpdate) => void | Promise<void>;

/**
 * Subscription型
 */
interface Subscription {
    unsubscribe(): void;
}

/**
 * GraphQL Subscriptionレスポンス型
 */
interface SubscriptionResponse {
    value: {
        data: {
            onNotifySaleStatus: {
                requestId: string;
                fileId: string;
                accountId: string;
                userId: string;
                saleStatus: SaleStatus;
                saleCount?: number;
            };
        };
    };
}

/**
 * 外販送信のリアルタイム通知を管理するサービス
 * 
 * 機能:
 * - リクエストごとにWebSocket接続を管理
 * - 外販送信のステータス更新をリアルタイムで受信
 * - 成功/失敗時に自動的にsubscriptionをクリーンアップ
 */
export class FileSaleSubscriptionService {
    /** リクエストIDをキーにしたWebSocket subscription管理 */
    private readonly subscriptionMap = new Map<string, Subscription>();
    
    /** リクエストIDをキーにしたコールバック関数管理 */
    private readonly callbackMap = new Map<string, SaleStatusCallback>();
    
    /** リクエストIDをキーにしたタイムアウトタイマー管理 */
    private readonly timeoutMap = new Map<string, NodeJS.Timeout>();
    
    /** タイムアウト時間（ミリ秒）: 5分（300000ミリ秒） */
    private readonly TIMEOUT_MS = 5 * 60 * 1000;

    /**
     * 外販送信ステータスの監視を開始
     * 
     * @param requestId - 一意のリクエストID
     * @param fileId - 監視対象のファイルID
     * @param accountId - アカウントID
     * @param userId - ユーザーID
     * @param callback - ステータス更新時に実行されるコールバック
     */
    public async subscribe(
        requestId: string,
        fileId: string,
        accountId: string,
        userId: string,
        callback: SaleStatusCallback
    ): Promise<void> {
        // コールバックを登録（既存があれば上書き）
        this.callbackMap.set(requestId, callback);

        // WebSocket接続がある場合でも、コールバックがなかった場合は再接続が必要
        // （cleanupでコールバックだけ削除された場合の対策）
        const hasSubscription = this.subscriptionMap.has(requestId);
        
        if (hasSubscription) {
            return;
        }

        // WebSocket接続がない場合は新規開始
        await this.startSubscription(requestId, fileId, accountId, userId);
    }

    /**
     * 外販送信ステータスの監視を停止
     * WebSocket接続とコールバックの両方をクリーンアップ
     * 
     * @param requestId - 監視を停止するリクエストID
     */
    public unsubscribe(requestId: string): void {
        // Mapに登録されているタイムアウトを削除
        const timeoutId = this.timeoutMap.get(requestId);
        if (timeoutId) {
            clearTimeout(timeoutId);
            this.timeoutMap.delete(requestId);
        }
        
        // Mapに登録されているsubscriptionを解除
        const subscription = this.subscriptionMap.get(requestId);
        if (subscription) {
            try {
                subscription.unsubscribe();
            } catch (error) {
                console.error(`[FileSaleSubscription] Error during unsubscribe for requestId: ${requestId}`, error);
            }
            this.subscriptionMap.delete(requestId);
        }
        
        this.callbackMap.delete(requestId);
    }

    /**
     * WebSocket subscriptionを開始
     */
    private async startSubscription(
        requestId: string,
        fileId: string,
        accountId: string,
        userId: string
    ): Promise<void> {
        try {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const observable: any = API.graphql(
                graphqlOperation(subscriptions.onNotifySaleStatus, {
                    requestId,
                    fileId,
                    accountId,
                    userId,
                })
            );

            const subscription = observable.subscribe({
                next: (response: SubscriptionResponse) => {
                    this.handleStatusUpdate(response);
                },
                error: (error: unknown) => {
                    this.handleSubscriptionError(requestId, fileId, error);
                },
            });

            this.subscriptionMap.set(requestId, subscription);
            // タイムアウトタイマーを設定
            this.startTimeout(requestId);
        } catch (error) {
            console.error(`[FileSaleSubscription] Failed to start subscription for requestId: ${requestId}`, error);
            // GraphQL検証エラーなどで失敗した場合、コールバックにエラーを通知
            await this.notifyCallback(requestId, { requestId, fileId, status: 'error' });
            // エラー時はsubscriptionとコールバックをクリーンアップ
            this.unsubscribe(requestId);
        }
    }

    /**
     * ステータス更新を処理
     */
    private async handleStatusUpdate(response: SubscriptionResponse): Promise<void> {
        const result = response.value.data.onNotifySaleStatus;
        if (!result?.requestId || !result?.fileId || !result?.saleStatus) {
            console.warn('[FileSaleSubscription] Invalid subscription response:', response);
            return;
        }

        // 既にクリーンアップ済み（unsubscribe後）の通知は無視
        if (!this.callbackMap.has(result.requestId)) {
            return;
        }

        // コールバックを実行
        await this.notifyCallback(result.requestId, {
            requestId: result.requestId,
            fileId: result.fileId,
            status: result.saleStatus,
            saleCount: result.saleCount,
        });

        // 終了ステータス（成功/失敗）の場合は自動的にクリーンアップ
        if (this.isFinalStatus(result.saleStatus)) {
            this.unsubscribe(result.requestId);
        }
    }
    
    /**
     * タイムアウト開始処理
     * 指定時間内に成功/失敗通知を受け取れない場合、自動的にunsubscribeする
     */
    private startTimeout(requestId: string): void {
        const timeoutId = setTimeout(() => {
            this.unsubscribe(requestId);
        }, this.TIMEOUT_MS);
        this.timeoutMap.set(requestId, timeoutId);
    }

    /**
     * Subscriptionエラーを処理
     */
    private async handleSubscriptionError(requestId: string, fileId: string, error: unknown): Promise<void> {
        console.error(`[FileSaleSubscription] Subscription error for requestId: ${requestId}`, error);
        
        // コールバックにエラーを通知
        await this.notifyCallback(requestId, { requestId, fileId, status: 'error' });
        
        // クリーンアップ
        this.unsubscribe(requestId);
    }

    /**
     * コールバックを実行
     */
    private async notifyCallback(requestId: string, data: SaleStatusUpdate): Promise<void> {
        const callback = this.callbackMap.get(requestId);
        if (callback) {
            try {
                await callback(data);
            } catch (error) {
                console.error(`[FileSaleSubscription] Callback error for requestId: ${requestId}`, error);
            }
        }
    }

    /**
     * 終了ステータスかどうかを判定
     */
    private isFinalStatus(status: SaleStatus): boolean {
        return status === 'success' || status === 'failure';
    }
}

export const fileSaleSubscriptionService = new FileSaleSubscriptionService();
